import React, { useContext } from "react";

import cx from "classnames";
import { addMonths, format, getDay, isThisYear } from "date-fns";

import { AppContext } from "~/common";
import { RadioInput } from "~/feedback/FormInputs";
import { Button, Checkbox, useModals } from "~/ui";
import { DateSelector, Modal } from "~/ui/legacyFellowIndex";
import Selector, {
	components,
	type ControlProps,
	type SelectorOption,
	type SingleValue,
} from "~/ui/selectors/Selector";

import { getWeekOfMonthText, weekDays as weekDaysList } from "../hooks/useRecurrenceOptions";

import type {
	CustomRecurringRuleSerializerInput,
	Choices as DayChoices,
	frequency as FrequencyType,
	monthly_repeat as MonthlyRepeatType,
} from "~/__generated__/useSaveEventMutationMutation.graphql";

import styles from "./CustomRecurrenceRuleModal.module.css";

function DateInputValueDisplay({ date, disabled }: { date: Date; disabled?: boolean }) {
	const label = React.useMemo(() => {
		let options: Intl.DateTimeFormatOptions = { day: "numeric", weekday: "long", month: "long" };

		if (!isThisYear(date)) {
			options = {
				...options,
				year: "numeric",
			};
		}

		return new Intl.DateTimeFormat("en-US", options).format(date);
	}, [date]);

	return (
		<Button variant="secondary" size="normal" type="button" disabled={disabled} className={styles.dateInput}>
			{label}
		</Button>
	);
}

type Props = {
	value: CustomRecurringRuleSerializerInput;
	setValue: (value: CustomRecurringRuleSerializerInput) => void;
	onClose: (result: boolean) => void;
	eventStart?: Date;
};

function intervalToNumber(val: string, defaultValue: number) {
	if (!val) return 1;
	const intVal = parseInt(val);
	if (1 <= intVal && intVal <= 500) {
		return intVal;
	} else {
		return defaultValue;
	}
}

export function useRecurrenceRuleModal() {
	const modals = useModals();
	return React.useCallback(
		(props: Omit<Props, "onClose">) => {
			return modals.open<boolean>(({ close }) => <CustomRecurrenceRuleModal {...props} onClose={close} />);
		},
		[modals],
	);
}

function RoundedSelectorControl<T extends SelectorOption>({ children, ...props }: ControlProps<T, false>) {
	return (
		<components.Control {...props} className={styles.roundedControl}>
			{children}
		</components.Control>
	);
}

export function CustomRecurrenceRuleModal({ value, setValue, onClose, eventStart = new Date() }: Props) {
	const [interval, setInterval] = React.useState<string>(value.interval.toString());
	const [frequency, setFrequency] = React.useState<FrequencyType>(value.frequency);
	const [weekDays, setWeekDays] = React.useState<DayChoices[]>(
		value.weekDays && value.weekDays.length !== 0
			? (value.weekDays as DayChoices[])
			: [weekDaysList[getDay(eventStart)]],
	);

	const [rruleEnds, setRruleEnds] = React.useState(value.untilDate ? "ON" : value.untilCount ? "AFTER" : "NEVER");
	const [untilDate, setUntilDate] = React.useState<Date>(
		value.untilDate ? new Date(value.untilDate) : addMonths(eventStart, 3),
	);
	const [untilOccurrences, setUntilOccurrences] = React.useState<string>(
		value.untilCount ? value.untilCount.toString() : "12",
	);
	const [monthlyRepeatType, setMonthlyRepeatType] = React.useState<MonthlyRepeatType>(
		value.monthlyRepeat || "ABSOLUTE",
	);

	const freqOptions = React.useMemo(
		() => [
			{ label: interval === "1" ? "Day" : "Days", value: "DAILY" },
			{ label: interval === "1" ? "Week" : "Weeks", value: "WEEKLY" },
			{ label: interval === "1" ? "Month" : "Months", value: "MONTHLY" },
			{ label: interval === "1" ? "Year" : "Years", value: "YEARLY" },
		],
		[interval],
	);
	const onFreqChange = React.useCallback(
		(freq: SingleValue<SelectorOption>) => {
			if (!freq) return;
			const value = freq.value as FrequencyType;
			setFrequency(value);

			if (weekDays.length === 0) {
				setWeekDays([weekDaysList[getDay(eventStart)]]);
			}
		},
		[weekDays, eventStart],
	);

	const onDone = React.useCallback(() => {
		setValue({
			interval: intervalToNumber(interval, 1),
			frequency,
			...(frequency === "WEEKLY" ? { weekDays } : {}),
			...(rruleEnds === "ON" ? { untilDate: untilDate.toISOString() } : {}),
			...(rruleEnds === "AFTER" ? { untilCount: intervalToNumber(untilOccurrences, 12) } : {}),
			...(frequency === "MONTHLY" ? { monthlyRepeat: monthlyRepeatType } : {}),
		});
		onClose(true);
	}, [frequency, interval, monthlyRepeatType, onClose, rruleEnds, setValue, untilDate, untilOccurrences, weekDays]);

	const onModalClose = React.useCallback(() => {
		onClose(false);
	}, [onClose]);

	const onChangeInterval = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setInterval(event.target.value);
	}, []);

	const onChangeUntilOccurrences = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		setUntilOccurrences(event.target.value);
	}, []);

	const onChangeRrule = React.useCallback((val: string) => {
		if (!val) return;
		setRruleEnds(val);
	}, []);

	const monthlyOptions: { label: string; value: MonthlyRepeatType }[] = React.useMemo(
		() => [
			{ label: `Monthly on the ${format(eventStart, "do")}`, value: "ABSOLUTE" },
			{ label: `Monthly on the ${getWeekOfMonthText(eventStart)}`, value: "RELATIVE" },
		],
		[eventStart],
	);
	const onMonthlyOptionChange = React.useCallback((opt: SingleValue<SelectorOption>) => {
		if (!opt) return;
		setMonthlyRepeatType(opt.value as MonthlyRepeatType);
	}, []);

	return (
		<Modal className={styles.modal} onClose={onModalClose} width="340px">
			<Modal.Header>
				<Modal.Title>Custom recurrence</Modal.Title>
				<Modal.HeaderActions onClose={onModalClose} />
			</Modal.Header>
			<Modal.Body>
				<div className={styles.inputHeader}>Repeat every</div>
				<div className={styles.repeatWrapper}>
					<input
						type="number"
						max="500"
						min="1"
						value={interval}
						onChange={onChangeInterval}
						className={cx(styles.numberInput, { [styles.error]: !interval || interval === "0" })}
					/>

					<Selector
						value={freqOptions.find(o => o.value === frequency)}
						options={freqOptions}
						onChange={onFreqChange}
						width="auto"
						height="30px"
						className={styles.selectInput}
						components={{
							Control: RoundedSelectorControl,
						}}
					/>
				</div>

				{frequency === "WEEKLY" && (
					<div style={{ marginBottom: "16px" }}>
						<div className={styles.inputHeader}>Repeat on</div>
						<DaysSelector value={weekDays || []} setValue={setWeekDays} />
					</div>
				)}

				{frequency === "MONTHLY" && (
					<div style={{ marginBottom: "16px" }}>
						<div className={styles.inputHeader}>Repeat</div>
						<Selector
							value={monthlyOptions.find(o => o.value === monthlyRepeatType)}
							options={monthlyOptions}
							onChange={onMonthlyOptionChange}
							width="auto"
							height="30px"
							className={styles.selectInput}
							components={{
								Control: RoundedSelectorControl,
							}}
						/>
					</div>
				)}

				<div>
					<div className={styles.inputHeader} style={{ marginBottom: 0 }}>
						Custom rule ends
					</div>

					<div className={styles.rruleWrapper}>
						<div className={styles.rruleRadioInputs}>
							<RadioInput
								name="ruleEnds"
								value={rruleEnds}
								options={[
									{
										value: "NEVER",
										label: "Never",
									},
									{
										value: "ON",
										label: "On",
									},
									{
										value: "AFTER",
										label: "After",
									},
								]}
								onChange={onChangeRrule}
								className={styles.radioInput}
							/>
						</div>

						<div className={styles.rruleSelects}>
							<div className={styles.rruleSelect}>
								<DateSelector date={untilDate} setDate={setUntilDate} disabled={rruleEnds !== "ON"}>
									<DateInputValueDisplay date={untilDate} disabled={rruleEnds !== "ON"} />
								</DateSelector>
							</div>

							<div className={styles.rruleSelect}>
								<div className={styles.occurrencesWrapper}>
									<input
										type="number"
										max="500"
										min="1"
										disabled={rruleEnds != "AFTER"}
										value={untilOccurrences}
										onChange={onChangeUntilOccurrences}
										className={cx(styles.numberInput, {
											[styles.error]: !untilOccurrences || untilOccurrences === "0" || untilOccurrences === "1",
										})}
									/>

									<div className={styles.occurrencesLabel}>
										{untilOccurrences === "1" ? "Occurrence" : "Occurrences"}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</Modal.Body>

			<Modal.Footer>
				<Button onClick={onModalClose}>Cancel</Button>

				<Button
					disabled={
						!interval ||
						interval === "0" ||
						((!untilOccurrences || untilOccurrences === "0" || untilOccurrences === "1") && rruleEnds === "AFTER")
					}
					variant="primary"
					onClick={onDone}
				>
					Done
				</Button>
			</Modal.Footer>
		</Modal>
	);
}

function DaysSelector({
	value,
	setValue,
}: {
	value: DayChoices[];
	setValue: React.Dispatch<React.SetStateAction<DayChoices[]>>;
}) {
	const onCheckedPressed = React.useCallback(
		(day: DayChoices) => () => {
			setValue((currentValue: DayChoices[]) => {
				const isUncheck = currentValue.includes(day);
				if (currentValue.length === 1 && isUncheck) return currentValue;

				if (isUncheck) {
					return currentValue.filter(d => d != day);
				}

				return [...currentValue, day];
			});
		},
		[setValue],
	);

	const { user } = useContext(AppContext);
	const firstDay = user?.internalAttributes?.firstWeekDay;
	let days: DayChoices[] = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"];
	if (firstDay === "Mon") {
		days = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];
	} else if (firstDay === "Sat") {
		days = ["SA", "SU", "MO", "TU", "WE", "TH", "FR"];
	}

	return (
		<div className={styles.days}>
			{days.map(day => (
				<label key={day} htmlFor={`checkbox-${day}`}>
					<div className={styles.dayLabel}>{day[0]}</div>
					<Checkbox id={`checkbox-${day}`} checked={value.includes(day)} onCheckedChange={onCheckedPressed(day)} />
				</label>
			))}
		</div>
	);
}
